import { useAuth } from "AuthProvider";
import useFetch from "hooks/useFetch";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  IconButton,
  IconName,
  PageHeader,
  Skeleton,
  styles as ui,
  Tabs,
  useConfirm,
  useToast,
} from "stories/Admin";
import AddSpecialTimeModal from "./AddSpecialTimeModal/AddSpecialTimeModal";
import styles from "./SpecialPrayerSettings.module.scss";

interface TimeSection {
  id: number;
  time: string;
  info: string;
  active: boolean;
  expanded: boolean;
  date: string;
  startDate: string;
  endDate: string;
  sameAsIsha: boolean;
}

const SpecialPrayerSettings = () => {
  const { execute, isLoading, error } = useFetch<any>();
  const { orgNumber, selectedOrgNumber, mosqueName, selectedOrgName } =
    useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState<string>("jummah");
  const [jummahTimes, setJummahTimes] = useState<TimeSection[]>([]);
  const [eidTimes, setEidTimes] = useState<TimeSection[]>([]);
  const [taraweehTimes, setTaraweehTimes] = useState<TimeSection[]>([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchTimes = async (
      endpoint: string,
      setTimes: React.Dispatch<React.SetStateAction<TimeSection[]>>,
      timeKey: string,
    ) => {
      const response = await execute(endpoint, "GET");
      if (response) {
        setTimes(
          response.map((timeData: any) => ({
            id: timeData.id,
            time: timeData[timeKey],
            info: timeData.info,
            date: timeData.date,
            active: false,
            expanded: false,
            startDate: timeData.startDate || "",
            endDate: timeData.endDate || "",
            sameAsIsha: timeData.sameAsIsha ?? false,
          })),
        );
      }
    };

    const fetchAllTimes = async () => {
      await Promise.all([
        fetchTimes(
          `/api/v1/jummah/org/${selectedOrgNumber}`,
          setJummahTimes,
          "jummahTime",
        ),
        fetchTimes(
          `/api/v1/taraweeh/org/${selectedOrgNumber}`,
          setTaraweehTimes,
          "taraweehTime",
        ),
        fetchTimes(
          `/api/v1/eid/org/${selectedOrgNumber}`,
          setEidTimes,
          "eidTime",
        ),
      ]);
    };

    fetchAllTimes();
  }, [selectedOrgNumber]);

  const handleToggle = (
    setTimes: React.Dispatch<React.SetStateAction<TimeSection[]>>,
    index: number,
  ) => {
    setTimes((prevTimes) =>
      prevTimes.map((time, i) => ({
        ...time,
        active: i === index,
        expanded: i === index,
      })),
    );
  };

  const handleAddNewTime = () => {
    setShowModal(true);
  };

  const handleSaveNewTime = async (
    time: string,
    info: string,
    startDate: string,
    endDate: string,
    sameAsIsha: boolean = false,
  ) => {
    if (activeTab === "jummah" && (!startDate || !endDate)) {
      toast.error("Velg startdato og sluttdato for Jummah.");
      return;
    }

    const newDate = new Date().toISOString();
    let payload: any = {
      info,
      mosqueOrgNr: selectedOrgNumber,
      date: newDate,
      startDate,
      endDate,
    };

    if (activeTab === "jummah") {
      payload.jummahTime = time;
    } else if (activeTab === "taraweeh") {
      payload.taraweehTime = time;
      payload.sameAsIsha = sameAsIsha;
    } else if (activeTab === "eid") {
      payload.eidTime = time;
    }

    const endpoint =
      activeTab === "jummah"
        ? "/api/v1/jummah"
        : activeTab === "taraweeh"
          ? "/api/v1/taraweeh"
          : "/api/v1/eid";

    const response = await execute(endpoint, "POST", payload);
    if (response) {
      const newTimeSection: TimeSection = {
        id: response.id,
        time,
        info,
        active: false,
        expanded: true,
        date: newDate,
        startDate: activeTab === "jummah" ? startDate : "",
        endDate: activeTab === "jummah" ? endDate : "",
        sameAsIsha,
      };

      if (activeTab === "jummah") {
        setJummahTimes((prevTimes) => [...prevTimes, newTimeSection]);
      } else if (activeTab === "taraweeh") {
        setTaraweehTimes((prevTimes) => [...prevTimes, newTimeSection]);
      } else if (activeTab === "eid") {
        setEidTimes((prevTimes) => [...prevTimes, newTimeSection]);
      }
    }
  };

  const handleDeleteTime = async (id: number) => {
    const ok = await confirm({
      title: `Slette ${TAB_INFO[activeTab]?.name ?? ""}-tiden?`,
      message: "Tiden fjernes fra appen og TV-skjermen.",
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (!ok) return;

    let endpoint = "";

    if (activeTab === "jummah") {
      endpoint = `/api/v1/jummah/${id}`;
    } else if (activeTab === "taraweeh") {
      endpoint = `/api/v1/taraweeh/${id}`;
    } else if (activeTab === "eid") {
      endpoint = `/api/v1/eid/${id}`;
    }

    execute(endpoint, "DELETE")
      .then(() => {
        if (activeTab === "jummah") {
          const updatedTimes = jummahTimes.filter((time) => time.id !== id);
          setJummahTimes(updatedTimes);
        } else if (activeTab === "taraweeh") {
          const updatedTimes = taraweehTimes.filter((time) => time.id !== id);
          setTaraweehTimes(updatedTimes);
        } else if (activeTab === "eid") {
          const updatedTimes = eidTimes.filter((time) => time.id !== id);
          setEidTimes(updatedTimes);
        }
      })
      .catch((error) => {
        console.error("Error deleting time:", error);
      });
  };

  const renderTimeSections = (
    times: TimeSection[],
    setTimes: React.Dispatch<React.SetStateAction<TimeSection[]>>,
    title: string,
  ) => {
    if (times.length === 0) {
      return (
        <EmptyState
          icon={TAB_INFO[activeTab].icon}
          title={`Ingen ${title.toLowerCase()} ennå`}
        >
          Legg til en tid, så vises den i appen og på TV-skjermen.
        </EmptyState>
      );
    }

    return (
      <div className={ui.g3}>
        {times.map((time, index) => (
          <Card key={time.id} className={styles.timeCard}>
            <span className={styles.timeHead}>
              <span className={styles.timeIcon}>
                <Icon name="mosque" size={20} />
              </span>
              <span className={styles.timeText}>
                <span className={styles.timeLabel}>
                  {`${index + 1}. ${TAB_INFO[activeTab].label}`}
                </span>
                <b className={`${ui.num} ${styles.timeValue}`}>
                  {activeTab === "taraweeh" && time.sameAsIsha
                    ? "Som Isha"
                    : time.time}
                </b>
              </span>
              <IconButton
                label="Slett"
                icon={<Icon name="trash" size={16} strokeWidth={2} />}
                onClick={() => handleDeleteTime(time.id)}
              />
            </span>
            {activeTab === "taraweeh" && time.sameAsIsha && (
              <span className={styles.meta}>
                <Icon name="info" size={14} strokeWidth={2} />
                Samme tid som Isha-jamat
              </span>
            )}
            {title === "Jummah tid" && (
              <span className={styles.meta}>
                <Icon name="cal" size={14} strokeWidth={2} />
                {formatDdMm(time.startDate)} – {formatDdMm(time.endDate)}
              </span>
            )}
            {time.info && <span className={styles.info}>{time.info}</span>}
          </Card>
        ))}
      </div>
    );
  };

  const lists: Record<
    string,
    [TimeSection[], React.Dispatch<React.SetStateAction<TimeSection[]>>, string]
  > = {
    jummah: [jummahTimes, setJummahTimes, "Jummah tid"],
    taraweeh: [taraweehTimes, setTaraweehTimes, "Taraweeh tid"],
    eid: [eidTimes, setEidTimes, "Eid tid"],
  };

  const [activeTimes, setActiveTimes, activeTitle] = lists[activeTab];
  // Taraweeh can only have one time.
  const canAdd = activeTab !== "taraweeh" || taraweehTimes.length === 0;

  return (
    <>
      <PageHeader
        crumb={mosqueName || selectedOrgName || undefined}
        title="Jummah, Taraweeh og Eid"
        subtitle="Spesielle bønner vises i appen og på TV-skjermen."
      />

      <div className={styles.tabRow}>
        <Tabs
          label="Type bønn"
          className={styles.tabs}
          value={activeTab}
          onChange={(k) => setActiveTab(k)}
          tabs={Object.keys(TAB_INFO).map((key) => ({
            value: key,
            label: (
              <>
                <Icon name={TAB_INFO[key].icon} size={16} />
                {TAB_INFO[key].label}
              </>
            ),
            extra: (
              <Badge tone={key === activeTab ? "mint" : "grey"}>
                {lists[key][0].length}
              </Badge>
            ),
          }))}
        />
        {canAdd && (
          <Button
            className={styles.addButton}
            icon={<Icon name="plus" size={16} strokeWidth={2.2} />}
            onClick={handleAddNewTime}
          >
            Legg til {TAB_INFO[activeTab].name}-tid
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className={ui.g3} aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <Skeleton height={40} width="60%" />
              <Skeleton height={14} width="40%" />
            </Card>
          ))}
        </div>
      ) : (
        renderTimeSections(activeTimes, setActiveTimes, activeTitle)
      )}

      {error && (
        <Alert tone="error">
          Noe gikk galt: {error.text || "Prøv igjen senere."}
        </Alert>
      )}

      <Alert tone="info">
        Taraweeh kan ha én tid. Velg «Samme tid som Isha-jamat» hvis den følger
        Isha.
      </Alert>

      <AddSpecialTimeModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveNewTime}
        activeTab={activeTab}
        jummahCount={jummahTimes.length + 1}
      />
    </>
  );
};

const TAB_INFO: Record<
  string,
  { label: string; name: string; icon: IconName }
> = {
  jummah: { label: "Jummah", name: "jummah", icon: "mosque" },
  taraweeh: { label: "Taraweeh", name: "taraweeh", icon: "moon" },
  eid: { label: "Eid", name: "eid", icon: "star" },
};

const MONTHS_SHORT = [
  "jan",
  "feb",
  "mar",
  "apr",
  "mai",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "des",
];

/** «01-03» → «1. mar». Leaves other formats as they are. */
const formatDdMm = (value: string) => {
  const match = /^(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const month = MONTHS_SHORT[parseInt(match[2], 10) - 1];
  return month ? `${parseInt(match[1], 10)}. ${month}` : value;
};

export default SpecialPrayerSettings;
